'use client'

import styled from '@emotion/styled'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

const APP_STORE_URL =
  'https://apps.apple.com/ch/app/polar-tensor/id6757867932?l=en-GB'
const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.polartensor.app&pli=1'

interface StoreBadgesProps {
  variant?: 'dark' | 'light'
  direction?: 'row' | 'column'
}

const AppleIcon = ({ color = 'white' }: { color?: string }) => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill={color}>
    <path d='M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z' />
  </svg>
)

const PlayIcon = ({ color = 'white' }: { color?: string }) => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill={color}>
    <path d='M3 20.5v-17c0-.89.95-1.44 1.71-.97l14.13 8.5c.73.44.73 1.5 0 1.94l-14.13 8.5c-.76.47-1.71-.08-1.71-.97z' />
  </svg>
)

const BadgeBox = styled.div<{
  $variant: 'dark' | 'light'
  $disabled?: boolean
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  transition: all 0.2s;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

  background: ${({ $variant }) =>
    $variant === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'};
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};

  &:hover {
    background: ${({ $variant, $disabled }) =>
      $disabled
        ? undefined
        : $variant === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.06)'};
  }
`

const BadgeLink = styled.a`
  text-decoration: none;
`

export default function StoreBadges({
  variant = 'dark',
  direction = 'row',
}: StoreBadgesProps) {
  const textColor = variant === 'dark' ? 'rgba(255,255,255,0.7)' : '#555'
  const labelColor =
    variant === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)'
  const iconColor = variant === 'dark' ? 'white' : '#555'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: direction,
        gap: 1.5,
      }}
    >
      <BadgeLink href={APP_STORE_URL} target='_blank' rel='noopener noreferrer'>
        <BadgeBox $variant={variant}>
          <AppleIcon color={iconColor} />
          <Box>
            <Typography
              sx={{ fontSize: '0.625rem', color: labelColor, lineHeight: 1 }}
            >
              Download on
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: textColor,
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            >
              App Store
            </Typography>
          </Box>
        </BadgeBox>
      </BadgeLink>
      <BadgeLink href={GOOGLE_PLAY_URL} target='_blank' rel='noopener noreferrer'>
        <BadgeBox $variant={variant}>
          <PlayIcon color={iconColor} />
          <Box>
            <Typography
              sx={{ fontSize: '0.625rem', color: labelColor, lineHeight: 1 }}
            >
              Get it on
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: textColor,
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            >
              Google Play
            </Typography>
          </Box>
        </BadgeBox>
      </BadgeLink>
    </Box>
  )
}

export { AppleIcon, APP_STORE_URL, GOOGLE_PLAY_URL, PlayIcon }
